/**
 * PDEV3-34【3】 — Guard against React + browser auto-translation crashes.
 *
 * Browser translators (Google / Edge / etc.) rewrite text nodes in the DOM
 * (e.g. wrapping them in <font> elements) behind React's back. This desyncs
 * React's virtual DOM from the real DOM, so on the next reconciliation React
 * may call removeChild / insertBefore on a node the translator has moved —
 * throwing a NotFoundError. That error is uncaught and tears down the whole
 * React tree (white screen), most visibly on dynamic screens (image/video
 * pickers, paginated lists, tabbed modals, etc.).
 *
 * These wrappers turn that fatal crash into a harmless no-op when the node is
 * not a child of the expected parent. Normal operations are unaffected (a
 * single cheap parentNode comparison).
 *
 * Ref: https://github.com/facebook/react/issues/11538
 *
 * Must be imported before ReactDOM mounts anything (first import in index.tsx).
 */
if (typeof Node === 'function' && Node.prototype) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proto = Node.prototype as any;

  const originalRemoveChild = proto.removeChild;
  proto.removeChild = function removeChild(child: Node) {
    if (child && child.parentNode !== this) {
      // The translator moved/replaced this node — skip instead of throwing.
      return child;
    }
    return originalRemoveChild.call(this, child);
  };

  const originalInsertBefore = proto.insertBefore;
  proto.insertBefore = function insertBefore(newNode: Node, referenceNode: Node | null) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // Reference node was moved out by the translator — append instead of throwing.
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  };
}

export {};
